import { ElementRef, Injectable } from '@angular/core';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { BehaviorSubject, Observable, Subject, combineLatest, debounceTime, distinctUntilChanged, filter, finalize, fromEvent, interval, map, merge, of, pipe, startWith, switchMap, take, takeUntil, tap } from 'rxjs';

import { filterAllTruthyInTuple, pairBehaviorSubjects } from 'core/rx-js';
import { FormControlService } from '../form-control.service';
import { FormControlServiceBase } from '../types';

export type MultiAutocompleteByKeyFn<T = any> = (key: (string | number | Record<string, string | number>)[]) => Observable<T[]>
export type MultiAutocompleteLoadFn<T = any> = (search: string, skip: number, take: number) => Observable<T[]>

@Injectable()
export class MultiAutocompleteFormControlService<T extends object = any> implements FormControlServiceBase<'multiautocomplete'> {

  readonly valueSubject = new BehaviorSubject<any[]>(null);
  get value() {
    return this.valueSubject.value
  }

  readonly #destroy = new Subject<void>()


  readonly loadFnSubject = new BehaviorSubject<MultiAutocompleteLoadFn<T>>(null);
  readonly initialSelectedItemsSubject = new BehaviorSubject<T[]>(null);

  readonly valueExprSubject = new BehaviorSubject<string>('value');
  get valueExpr() {
    return this.valueExprSubject.value
  }

  readonly stopFetchingSubject = new BehaviorSubject<boolean>(false);
  readonly displayExprSubject = new BehaviorSubject<string>('display');
  readonly pageSizeSubject = new BehaviorSubject<number>(10);
  readonly fetchingSubject = new BehaviorSubject<boolean>(false);
  readonly inputSubject = new BehaviorSubject<ElementRef<HTMLInputElement>>(null);
  get input() {
    return this.inputSubject.value
  }

  readonly selectedItemsSubject = new BehaviorSubject<T[]>([]);
  set selectedItems(selectedItems) {
    this.selectedItemsSubject.next([...new Set(selectedItems || [])])
  }
  get selectedItems() {
    return this.selectedItemsSubject.value
  }

  readonly dataSubject = new BehaviorSubject<any[]>(null);
  get data() {
    return this.dataSubject.value;
  }
  readonly searchSubject = new BehaviorSubject<string>(null);
  readonly pageIdxSubject = new BehaviorSubject<number>(0);
  get pageIdx() {
    return this.pageIdxSubject.value
  }

  readonly autocompleteSubject = new BehaviorSubject<MatAutocomplete>(null);
  get autocomplete() {
    return this.autocompleteSubject.value
  }

  readonly autocompleteTriggerSubject = new BehaviorSubject<MatAutocompleteTrigger>(null);
  get autocompleteTrigger() {
    return this.autocompleteTriggerSubject.value
  }

  readonly inputValueChangedObs = this.inputSubject.pipe(
    filter(Boolean),
    switchMap((input) =>
      merge(
        fromEvent(input.nativeElement, 'change').pipe(startWith(null)),
        fromEvent(input.nativeElement, 'keyup')
      ).pipe(
        map(() => input.nativeElement.value)
      )
    ),
    distinctUntilChanged(),
    debounceTime(300)
  )

  readonly scrollEndReachedObs = this.autocompleteSubject.pipe(
    filter(Boolean),
    switchMap((autocomplete) => merge(
      autocomplete.opened.pipe(map(() => true)),
      autocomplete.closed.pipe(map(() => false))
    )
      .pipe(
        startWith(autocomplete.isOpen),
        filter(Boolean),
        switchMap(() => interval(100).pipe(
          map(() => autocomplete.panel?.nativeElement),
          filter(Boolean),
          take(1)
        ))
      )
    ),
    distinctUntilChanged(),
    filter(Boolean),
    switchMap((panelElement) => fromEvent(panelElement, 'scroll').pipe(
      map(() => (panelElement.scrollTop + panelElement.clientHeight) > (panelElement.scrollHeight - 75)))
    ),
    distinctUntilChanged()
  )

  readonly displayedDataObs = combineLatest([this.dataSubject, this.displayExprSubject, this.valueExprSubject, this.selectedItemsSubject]).pipe(
    filterAllTruthyInTuple(),
    map(([data, displayExpr, valueExpr, selectedItems]) => data.map((row) => ({
      ...row,
      value: row[valueExpr],
      display: row[displayExpr],
      selected: !!selectedItems.find((selectedItem) => this.#itemsMatchByValue(row, selectedItem, valueExpr))
    })))
  )

  readonly displayWithObs = this.displayExprSubject.pipe(
    filter(Boolean),
    map((displayExpr) => (value: any) => {
      return value[displayExpr]
    })
  )

  readonly focusedSubject = this._formControlService.focusedSubject;


  constructor(
    private readonly _formControlService: FormControlService
  ) {



  }

  initialize() {
    this._initSearchSubscription()

    this.focusedSubject.pipe(
      filter(Boolean),
      take(1),
      takeUntil(this.#destroy)
    ).subscribe(() => {
      this._initFetchDataSubscription();
    })

    this._matchPageIdxOnEndReached();
    this._resetDataOnSearch();

    pairBehaviorSubjects(this._formControlService.valueSubject, this.valueSubject);
    this._boundItemAndValue();


    this.focusedSubject.pipe(
      filter(Boolean),
      filter(() => !this.autocomplete.isOpen),
      takeUntil(this.#destroy)
    )
      .subscribe(() => {
        this.autocompleteTrigger.openPanel();
      })
  }


  private _boundItemAndValue() {

    combineLatest([this.valueSubject, this.initialSelectedItemsSubject, this.dataSubject, this.valueExprSubject]).pipe(
      takeUntil(this.#destroy),
      filter(([, initialSelectedItems, data, valueExpr]) => Boolean((initialSelectedItems || data) && valueExpr)),
      filter(([value, , , valueExpr]) => {
        if (!this.selectedItems) {
          return Boolean(value)
        }

        return value !== (this.selectedItems as any)[valueExpr]
      }),
      switchMap(([value, initialSelectedItems, data, valueExpr]) => {

        if (value === null || value === undefined) {
          return of([]);
        }


        const allPossibleItems = pipe(
          () => this.mergeSelectedItems(data, initialSelectedItems),
          (items) => this.mergeSelectedItems(items, this.selectedItems)
        )(null);

        const fromDataSelectedItems = allPossibleItems?.filter?.((item: any) => value.includes(item[valueExpr || '']));

        if (fromDataSelectedItems) {
          return of(fromDataSelectedItems);
        }

        if (!initialSelectedItems) {
          this.valueSubject.next([]);
          return of([]);
        }

        return initialSelectedItems.filter((item: any) => value.includes(item[valueExpr || '']))
      }),
      filter((selectedItems) => JSON.stringify(selectedItems) !== JSON.stringify(this.selectedItems)),
      takeUntil(this.#destroy)
    )
      .subscribe((selectedItems) => {
        this.selectedItems = selectedItems as T[];
      })


    combineLatest([this.selectedItemsSubject, this.valueExprSubject]).pipe(
      takeUntil(this.#destroy),
      filterAllTruthyInTuple(),
      map(([selectedItems, valueExpr]) => selectedItems.map(item => (item as any)[valueExpr])),
      filter((value) => JSON.stringify(value) !== JSON.stringify(this.value))
    )
      .subscribe((value) => {

        this.valueSubject.next(value);
      })


  }



  private _initSearchSubscription() {

    this.inputValueChangedObs.pipe(
      takeUntil(this.#destroy)
    )
      .subscribe((search) => {
        this.searchSubject.next(search)
      })
  }

  private _initFetchDataSubscription() {

    combineLatest([this.loadFnSubject, this.stopFetchingSubject, this.searchSubject, this.pageSizeSubject, this.pageIdxSubject]).pipe(
      filter(([loadFn, stopFetching]) => !!loadFn && !stopFetching),
      takeUntil(this.#destroy),
      debounceTime(500),
      switchMap(([loadFn, _, search, pageSize, pageIdx]) => {
        this.fetchingSubject.next(true);
        return loadFn(search, pageSize * pageIdx, pageSize).pipe(
          tap(() => this.fetchingSubject.next(false)),
          finalize(() => this.fetchingSubject.next(false))
        )
      }),
      takeUntil(this.#destroy)
    )
      .subscribe((data) => {
        if (data.length < this.pageSizeSubject.value) {
          this.stopFetchingSubject.next(true)
        }
        this.dataSubject.next([...(this.data || []), ...data]);
      })




  }


  mergeSelectedItems(selectedItems1: T[], selectedItems2: T[]) {
    if (!selectedItems1) {
      return selectedItems2 || []
    }

    if (!selectedItems2) {
      return selectedItems1;
    }

    return [
      ...selectedItems1.filter((item: any) => !selectedItems2.some((item2: any) => item2[this.valueExpr] === item[this.valueExpr])),
      ...selectedItems2
    ]
  }

  private _matchPageIdxOnEndReached() {

    this.scrollEndReachedObs.pipe(
      filter(Boolean),
      takeUntil(this.#destroy)
    )
      .subscribe(() => {
        this.pageIdxSubject.next(this.pageIdx + 1)
      })
  }

  private _resetDataOnSearch() {

    combineLatest([this.loadFnSubject, this.searchSubject]).pipe(
      takeUntil(this.#destroy)
    )
      .subscribe(() => {
        this.pageIdxSubject.next(0);
        this.dataSubject.next(null);
        this.stopFetchingSubject.next(false);

      })

  }



  #itemsMatchByValue(item1: T, item2: T, valueExpr: string) {

    return JSON.stringify((item1 as any)[valueExpr]) === JSON.stringify((item2 as any)[valueExpr])
  }

  #itemSelected(item: any) {
    return this.selectedItems.some((innerItem: any) => innerItem[this.valueExpr] === item[this.valueExpr])
  }


  select(item: T) {

    if (this.selectedItems && this.#itemSelected(item)) {
      this.selectedItems = this.selectedItems.filter((selectedItem) => (item as any)[this.valueExpr] !== (selectedItem as any)[this.valueExpr]);
      return;
    }
    this.searchSubject.next('');
    this.input.nativeElement.value = '';
    this.selectedItems = [...(this.selectedItems || []), item];
  }


  ngOnDestroy() {
    this.#destroy.next()
    this.#destroy.complete()
  }

}



